import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Youtube } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Footer = () => {
  const [services, setServices] = useState([]);

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/services`);
        const data = await response.json();

        if (data.success && data.services) {
          const activeServices = data.services
            .filter(service => service.is_active !== false)
            .slice(0, 6)
            .map(service => service.name);
          setServices(activeServices);
        }
      } catch (error) {
        console.error('Error fetching services for footer:', error);
        setServices([]);
      }
    };

    fetchServices();
  }, []);

  return (
    <footer
      data-testid="main-footer"
      className="relative bg-slate-950 text-slate-300 border-t border-white/5 overflow-hidden"
    >
      {/* Glow accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[40rem] h-[20rem] bg-cyan-500/5 blur-3xl rounded-full" />
        <div className="absolute bottom-0 right-1/4 w-[30rem] h-[15rem] bg-blue-500/5 blur-3xl rounded-full" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img
                src="https://customer-assets.emergentagent.com/job_fcf8fe40-6235-4ae1-a5a5-ebd4002e0a55/artifacts/8jty1yq0_Gemini_Generated_Image_l9r4ujl9r4ujl9r4.png"
                alt="Propitstop Logo"
                className="h-12 w-12 object-cover rounded-xl ring-1 ring-white/10"
              />
              <div>
                <div className="font-extrabold text-xl text-white tracking-tight">Propitstop</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400">Car Services</div>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-5">
              Your trusted advisor for car service in Bangalore. Transparent pricing, honest reviews, and genuine care for your vehicle.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 text-slate-300 hover:text-cyan-400 flex items-center justify-center transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="https://www.youtube.com/@Sathishd-k5e"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                data-testid="social-youtube"
                className="w-10 h-10 rounded-xl bg-slate-900/60 border border-slate-800 hover:border-cyan-500/40 text-slate-300 hover:text-cyan-400 flex items-center justify-center transition-colors"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-5">Navigate</h3>
            <ul className="space-y-3">
              {quickLinks.map(link => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-5">Services</h3>
            <ul className="space-y-3">
              {services.length > 0 ? (
                services.map(service => (
                  <li key={service}>
                    <Link
                      to="/service/services"
                      className="text-sm text-slate-400 hover:text-cyan-400 transition-colors line-clamp-1"
                    >
                      {service}
                    </Link>
                  </li>
                ))
              ) : (
                <li className="text-slate-500 text-sm">Loading services...</li>
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-5">Reach Us</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                </div>
                <span className="text-sm text-slate-400">Bangalore, Karnataka 560077</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-cyan-400" />
                </div>
                <a
                  href="tel:+919110416663"
                  data-testid="footer-phone"
                  className="text-sm text-slate-300 hover:text-cyan-400 transition-colors"
                >
                  +91 91104 16663
                </a>
              </div>
              <div className="flex items-start gap-3">
                <div className="mt-0.5 w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-cyan-400" />
                </div>
                <a
                  href="mailto:support@propitstop.in"
                  data-testid="footer-email"
                  className="text-sm text-slate-300 hover:text-cyan-400 transition-colors break-all"
                >
                  support@propitstop.in
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-xs">
              © {new Date().getFullYear()} Propitstop.in. All rights reserved.
            </p>
            <div className="flex gap-6 text-xs">
              <a href="#" className="text-slate-500 hover:text-cyan-400 transition-colors">Privacy Policy</a>
              <a href="#" className="text-slate-500 hover:text-cyan-400 transition-colors">Terms of Service</a>
              <a href="#" className="text-slate-500 hover:text-cyan-400 transition-colors">Refund Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
